// One planned-dashboard card in the job detail view: the title/project/build
// badge header, purpose + notes, the created dashboard paths (or build error),
// and the grid of the screenshots that compose it. Purely presentational — the
// parent computes each build's status (done / building / queued) and passes it.

import { Loader2 } from 'lucide-react'
import type {
  GwbkAutogenShot,
  GwbkDashboardBuild,
  GwbkPlannedDashboard
} from '@axilon-apps/domains-ontology-ui-state'

import GwbkAutogenShotThumb from './GwbkAutogenShotThumb'

export interface GwbkAutogenPlanCardProps {
  jobId: string
  dash: GwbkPlannedDashboard
  /** This dashboard's build outcome, once it has finished (else null/undefined). */
  build: GwbkDashboardBuild | null | undefined
  /** The build is actively running (left the queue, not yet finished). */
  buildActive: boolean
  /** The build is waiting for a free builder slot. */
  buildQueued: boolean
  shotsByName: Map<string, GwbkAutogenShot>
}

const GwbkAutogenPlanCard = ({
  jobId,
  dash,
  build,
  buildActive,
  buildQueued,
  shotsByName
}: GwbkAutogenPlanCardProps) => {
  return (
    <div className="flex flex-col gap-2 rounded border border-line bg-bg-sunk/30 p-2.5">
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-ink-1">{dash.title}</span>
          {dash.project ? (
            <span className="rounded bg-bg-sunk px-1.5 py-0.5 text-10 text-ink-3">
              {dash.project}
            </span>
          ) : null}
          <span className="text-10 text-ink-3">
            {dash.shotNames.length} view{dash.shotNames.length === 1 ? '' : 's'}
          </span>
          {build ? (
            <span
              className={`ml-auto rounded px-1.5 py-0.5 text-10 ${
                build.completed
                  ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                  : 'bg-destructive/15 text-destructive'
              }`}
            >
              {build.completed ? 'Built' : 'Build failed'}
            </span>
          ) : buildActive ? (
            <span className="ml-auto flex items-center gap-1 text-10 text-brand">
              <Loader2 className="size-3 animate-spin" aria-hidden />
              Building…
            </span>
          ) : buildQueued ? (
            <span className="ml-auto rounded bg-bg-sunk px-1.5 py-0.5 text-10 text-ink-3">
              Queued
            </span>
          ) : null}
        </div>
        {dash.purpose ? <p className="m-0 text-xs text-ink-3">{dash.purpose}</p> : null}
        {dash.notes ? <p className="m-0 text-11 italic text-ink-3">{dash.notes}</p> : null}
        {build && build.dashboardPaths.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1 pt-0.5">
            {build.dashboardPaths.map((path) => (
              <span
                key={path}
                className="rounded bg-brand/10 px-1.5 py-0.5 font-mono text-10 text-brand"
                title={path}
              >
                {path}
              </span>
            ))}
          </div>
        ) : null}
        {build && !build.completed ? (
          <p className="m-0 text-11 text-destructive">
            {build.error ?? build.message ?? 'The builder did not finish this view.'}
          </p>
        ) : null}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {dash.shotNames.map((name) => {
          const shot = shotsByName.get(name)
          if (!shot) return null
          return (
            <GwbkAutogenShotThumb
              key={name}
              jobId={jobId}
              shot={shot}
              caption={shot.variant === 'page' ? shot.routeKey : shot.variant}
            />
          )
        })}
      </div>
    </div>
  )
}

export default GwbkAutogenPlanCard
